/**
 * Ground-truth arithmetic for the map picker.
 *
 * Every question this module answers comes from the same measured fact: a coordinate error is
 * invisible. The user cannot look at a pin and tell whether it is 20 m or 2 km off, so the code —
 * and through it, the UI — has to know what precision the current view actually delivers. The
 * numbers here are derived from the Web Mercator projection itself rather than copied from a
 * table, so they stay correct for any latitude and zoom the picker can reach.
 *
 * Pure functions, no DOM: these run in `node --test` (geo.test.ts) as well as in the browser.
 */

/**
 * The radius of the Web Mercator sphere, in metres.
 *
 * Web Mercator is not on the WGS-84 ellipsoid — it projects onto a sphere of radius 6 378 137 m,
 * which is what every slippy-map tile grid assumes. Using the ellipsoid's mean radius instead
 * would drift the numbers by a fraction of a percent: irrelevant for a ±600 m judgement, but this
 * is the value the tiles actually use, so it is the value written down.
 */
const MERCATOR_RADIUS = 6378137;

/** The edge of one tile in pixels. 256 is the size the OSM tile grid serves. */
const TILE_SIZE = 256;

/**
 * A fingertip contacts roughly 8 mm of glass, which on a 390 px-wide phone screen is a patch about
 * 46 px across; the centre of a tap can land up to half a patch away from where the user aimed.
 * That half-patch — 23 px — is the uncertainty a tap carries, in any direction, and it is what
 * `tapAccuracyMetres` converts into ground metres.
 */
const TAP_UNCERTAINTY_PX = 23;

/**
 * Ground metres per pixel at a zoom level and latitude. Web Mercator, 256 px tiles.
 *
 * The whole world is `2πR` metres across and rendered as `TILE_SIZE · 2^zoom` pixels at the
 * equator; ground scale shrinks with the cosine of the latitude, because Mercator stretches
 * north-south precisely by `1/cos(φ)` to keep shapes square.
 */
export function metresPerPixel(latitude: number, zoom: number): number {
  const latitudeRadians = (latitude * Math.PI) / 180;
  return (
    (2 * Math.PI * MERCATOR_RADIUS * Math.cos(latitudeRadians)) /
    (TILE_SIZE * 2 ** zoom)
  );
}

/**
 * The accuracy a finger-placed pin actually has, in metres.
 * A tap is not a pixel: the contact patch is roughly 23 px across, so the practical uncertainty is
 * that many pixels' worth of ground.
 */
export function tapAccuracyMetres(latitude: number, zoom: number): number {
  return TAP_UNCERTAINTY_PX * metresPerPixel(latitude, zoom);
}

/**
 * Whether a coordinate pair is usable at all. Rejects NaN, Infinity and out-of-range values.
 *
 * A pin at `lat: 108.45` (a Đà Lạt longitude in the latitude slot) would otherwise flow into the
 * plan, which would place the stop in Mongolia — silently, and plausibly enough to survive a
 * glance at a list. Range is checked here, once, rather than trusted from every caller.
 */
export function isUsableCoordinate(
  value: { lat: number; lon: number } | null,
): value is { lat: number; lon: number } {
  if (value === null) return false;
  return (
    Number.isFinite(value.lat) &&
    Number.isFinite(value.lon) &&
    value.lat >= -90 &&
    value.lat <= 90 &&
    value.lon >= -180 &&
    value.lon <= 180
  );
}

/** IUGG mean Earth radius, in metres. */
const MEAN_EARTH_RADIUS_M = 6371008.8;

/**
 * Great-circle distance between two coordinates, in metres.
 *
 * Haversine on the mean radius, which is exact enough for the one question it answers in the browser —
 * "which of my saved places is nearest to this pin?" — and needs no database. It is deliberately not
 * used for the duplicate rule: the 150 m gate stays in `find_duplicate_places`, because a second
 * distance rule is precisely the drift ADR-001 keeps the rule in SQL to prevent.
 */
export function distanceMetres(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
  const dLat = toRadians(b.lat - a.lat);
  const dLon = toRadians(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLon / 2) ** 2;
  // Clamped so a floating-point overshoot cannot make `asin` return NaN for antipodal points.
  return 2 * MEAN_EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
